import { HTMLAttributes, MouseEvent, useState } from "react";

/**
 * Shift+click range selection for a multi-select Autocomplete: a shift+click
 * on an option adds every selectable option between the last clicked option
 * and itself, in list order. A plain click keeps the Autocomplete behaviour.
 *
 * Returns a wrapper for the `props` given to `renderOption`.
 */
export const useShiftRangeSelect = <T>({
  options,
  value,
  onChange,
  isEqual,
  isSelectable = () => true,
  maxSelected,
}: {
  options: readonly T[];
  value: readonly T[];
  onChange: (value: T[], event: MouseEvent<HTMLLIElement>) => void;
  isEqual: (a: T, b: T) => boolean;
  isSelectable?: (option: T) => boolean;
  // A range can overshoot the selection cap: keep the first ones
  maxSelected?: number;
}) => {
  const [anchor, setAnchor] = useState<T | null>(null);

  return <P extends HTMLAttributes<HTMLLIElement>>(props: P, option: T): P => ({
    ...props,
    onClick: (event: MouseEvent<HTMLLIElement>) => {
      const from =
        anchor === null ? -1 : options.findIndex((o) => isEqual(o, anchor));
      const to = options.findIndex((o) => isEqual(o, option));

      if (event.shiftKey && from !== -1 && to !== -1) {
        const range = options
          .slice(Math.min(from, to), Math.max(from, to) + 1)
          .filter((o) => isSelectable(o) && !value.some((v) => isEqual(v, o)));
        const room =
          maxSelected === undefined
            ? range.length
            : Math.max(0, maxSelected - value.length);
        onChange([...value, ...range.slice(0, room)], event);
      } else {
        props.onClick?.(event);
      }
      // Rows outside `options` (ex: a "select all" row) cannot anchor a range
      if (to !== -1) {
        setAnchor(option);
      }
    },
  });
};
